import InputMask from 'react-input-mask';
import { isEmpty } from '@carecloud/cloudpak';
import React, { useRef } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { Icon, Header } from '..';
import { SingleDatePicker } from 'react-dates';
import { WEEKDAY_FORMAT, PREVIOUS_MONTH, NEXT_MONTH } from '../../../constants/DatePicker';
import calendarOnlyStyles from './datePickerCalendarOnly.module.scss';
import CustomInfoPanel from '../datePicker/CustomInfoPanel';
import styles from './input.module.scss';
import stylesWithCalendar from './inputWithCalendar.module.scss';
import { isMobileDevice } from '../../services/utilities';

const component = React.memo(
  ({
    id,
    type,
    name,
    label,
    state,
    openDirection = 'OPEN_DOWN',
    inputMode = 'numeric',
    enableCalendar,
    placeholder,
    required,
    requiredLabel,
    onChange,
    value,
    disabled,
    maskInput,
    dispatch,
    getValidRange,
    maskChar,
    theme,
    defaultValue,
    handleValidation,
    validations,
    errors,
    autoFocus,
    supportingText,
    updatedDate,
    radios,
    errorRequiredLabel,
    rawErrors = [],
    displayOneErrorOnly = false,
    disableCopy,
    disablePaste,
  }) => {
    const onChangeWithValueChanger = e => {
      const { value } = e.target;
      const valueAfterInitialSpace = value.replace(/^\s+/, '');
      const replicaEvent = { ...e };
      if ( !value || valueAfterInitialSpace.length > 0 ) {
        let returnValue = valueAfterInitialSpace;
        const regex = /\//gi;
        returnValue = returnValue.replace(regex, '-');
       if (type !=='text' && type !=='password' && moment.locale() === 'es' && moment(returnValue, 'DD-MM-YYYY', true).isValid()) {
          const initialDate = moment(returnValue, 'DD/MM/YYYY');
          const formattedDate = initialDate.format('MM/DD/YYYY');
          replicaEvent.target.value = formattedDate;
        }
        onChange(replicaEvent);
      }
    };
    // Segmented OTP boxes (e.g. the sign-in-with-code modal): each box holds
    // one digit, but the assembled string is what flows through onChange, so
    // validation/submission see it exactly like any other text input.
    const otpRefs = useRef([]);
    const otpLength = (maskInput || '').length || 6;
    const otpDigits = (value || '').split('');
    const focusOtpBox = index => {
      const el = otpRefs.current[index];
      el && el.focus();
    };
    const handleOtpDigitChange = (index, raw) => {
      const digit = raw.replace(/[^0-9]/g, '').slice(-1);
      const digits = [...otpDigits];
      while (digits.length < otpLength) digits.push('');
      digits[index] = digit;
      onChangeWithValueChanger({ target: { value: digits.join('').replace(/\s+$/, ''), id } });
      if (digit && index < otpLength - 1) focusOtpBox(index + 1);
    };
    const handleOtpKeyDown = (index, e) => {
      if (e.key === 'Backspace' && !otpDigits[index] && index > 0) focusOtpBox(index - 1);
    };
    const handleOtpBlur = () => {
      validations && handleValidation({ target: { value: value || '', id } });
    };
    return (
      <div>
        <div id={`${id}Wrapper`} className={classNames(styles[theme], styles.formGroup)}>
          {label && (
            <label htmlFor={name} className={styles.label}>
              {label}
              {!value && required && <span className={styles.required}>{requiredLabel || required}</span>}
            </label>
          )}
          <div className={classNames(enableCalendar ? stylesWithCalendar.inputWrapper : styles.inputWrapper, { [styles.inputWrapperWithRequired]: required })}>
            {/* eslint-disable-next-line no-nested-ternary */}
            {type === 'radios' ? (
              <fieldset
                style={{
                  margin: 'unset',
                  padding: 'unset',
                  border: 'unset',
                  width: '100%',
                }}
              >
                <legend />
                <div id="radios" className={styles.radioGroup}>
                  {radios.map(radio => (
                    <div key={radio} className={styles.radio}>
                      <label htmlFor={radio}>
                        <Header {...{ id: `${radio}-header`, label: radio, type: 'H6' }} />
                      </label>
                      <input
                        type="radio"
                        value={radio}
                        id={radio}
                        key={radio}
                        name="radios"
                        onChange={_ => onChange({ target: { value: radio, id } })}
                      />
                    </div>
                  ))}
                </div>
              </fieldset>
               // eslint-disable-next-line no-nested-ternary
            ) : type === 'otp' ? (
              <div className={styles.otpBoxes}>
                {Array.from({ length: otpLength }, (_, index) => (
                  // Fixed-length, position-based boxes (never
                  // reordered/added/removed), so the index is a genuinely
                  // stable identity here, same reasoning as radioInput.jsx's
                  // use of the same suppression below.
                  <input
                    key={index} // eslint-disable-line react/no-array-index-key
                    id={index === 0 ? id : `${id}-${index}`}
                    ref={el => { otpRefs.current[index] = el; }}
                    className={styles.otpBox}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    disabled={disabled}
                    value={otpDigits[index] || ''}
                    // maxLength=1 blocks typing a replacement digit once a box
                    // is already filled (e.g. correcting a wrong code after a
                    // failed verify) unless the existing character is
                    // selected first — without this, onChange silently never
                    // fires and the stale first digit stays in state (and
                    // gets sent to the mutation) even though the user thinks
                    // they retyped it.
                    onFocus={e => e.target.select()}
                    onChange={e => handleOtpDigitChange(index, e.target.value)}
                    onKeyDown={e => handleOtpKeyDown(index, e)}
                    onBlur={index === otpLength - 1 ? handleOtpBlur : undefined}
                  />
                ))}
              </div>
            ) : (
              enableCalendar ? (
                <div className={classNames(stylesWithCalendar.datePickerWrapper)}>
                  <InputMask
                    autoFocus={autoFocus}
                    id={id}
                    mask={maskInput}
                    maskChar={maskChar}
                    onChange={onChangeWithValueChanger}
                    style={{ flex: 1, background: isMobileDevice() && '#FFF' }}
                    placeholder={placeholder}
                    value={value}
                    defaultValue={defaultValue}
                    disabled={disabled}
                    type={type}
                    inputMode={inputMode}
                    onBlur={validations && handleValidation}
                    name={name}
                    onPaste={e => {
                      if (disablePaste) {
                        e.preventDefault();
                      }
                    }}
                    onCopy={e => {
                      if (disableCopy) {
                        e.preventDefault();
                      }
                    }}
                  />
                  <div className={classNames(calendarOnlyStyles.wrapper, { createVisitSummaryDatePickerFocused: state.focus })}>
                    <div className={calendarOnlyStyles.activeCalendarWrapper}>
                      <SingleDatePicker
                        id={id}
                        required
                        openDirection={openDirection}
                        showDefaultInputIcon
                        onOutsideClick={_ => dispatch({ focus: false })}
                        onFocusChange={({ focused }) => { 
                          dispatch({ focus: focused })
                        }}
                        keepOpenOnDateSelect
                        numberOfMonths={1}
                        date={value === '' ? null : moment(value, 'MM/DD/YYYY')}
                        onDateChange={date => {
                          //eslint-disable-next-line object-shorthand
                          onChangeWithValueChanger({ target: { value: moment(date).format('MM/DD/YYYY'), id: id  } })
                        }}
                        isOutsideRange={day => getValidRange(day)}
                        inputIconPosition="after"
                        displayFormat={String()}
                        navPrev={<Icon {...{ id: PREVIOUS_MONTH, theme: PREVIOUS_MONTH, icon: 'arrow-left' }} />}
                        navNext={<Icon {...{ id: NEXT_MONTH, theme: PREVIOUS_MONTH, icon: 'arrow-right' }} />}
                        focused={state.focus}
                        enableOutsideDays
                        hideKeyboardShortcutsPanel
                        weekDayFormat={WEEKDAY_FORMAT}
                        screenReaderInputMessage=""
                        calendarInfoPosition="bottom"
                        renderCalendarInfo={() => (
                          <CustomInfoPanel
                            handleSave={() => {
                              //eslint-disable-next-line object-shorthand
                              onChangeWithValueChanger({ target: { value: value, id: id  } })
                              dispatch({ focus: false })
                            }}
                            handleCancel={() => {
                              //eslint-disable-next-line object-shorthand
                              onChangeWithValueChanger({ target: { value: '', id: id  } })
                              dispatch({ focus: false })
                            }}
                            borderPosition='borderBottom' 
                            labels={{save: 'Save', cancel: 'Cancel'}}
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <InputMask
                  autoFocus={autoFocus}
                  id={id}
                  mask={maskInput}
                  maskChar={maskChar}
                  onChange={onChangeWithValueChanger}
                  style={{ flex: 1, background: isMobileDevice() && '#FFF' }}
                  placeholder={placeholder}
                  value={value}
                  defaultValue={defaultValue}
                  disabled={disabled}
                  type={type}
                  onBlur={validations && handleValidation}
                  name={name}
                  onPaste={e => {
                    if (disablePaste) {
                      e.preventDefault();
                    }
                  }}
                  onCopy={e => {
                    if (disableCopy) {
                      e.preventDefault();
                    }
                  }}
                />
              )
            )}
          </div>
        </div>
        {type === 'signature' && <div className={styles.signatureTime}>{updatedDate}</div>}
        {!isEmpty(rawErrors)
          ? rawErrors.map(error => (
            <div className={styles.errorMessage} key={error}>
              {errorRequiredLabel}
            </div>
            ))
          : errors &&
            !displayOneErrorOnly &&
            errors.map(error => (
              <div key={error.error_message} className={styles.errorMessage}>
                { errors?.icon ? <img src={errors.icon} alt='breeze-logo' /> : <></> }
                {error.error_message}
              </div>
            ))}
        {displayOneErrorOnly && (
          <div key={errors[0]?.error_message} className={styles.errorMessage}>
            { errors[0]?.icon ? <img src={errors[0]?.icon} alt='' /> : <></> }
            {errors[0]?.error_message}
          </div>
        )}
        {supportingText && (
          <div className={classNames(styles.supportingText, { [styles.supportingTextGreen]: value })}>
            {supportingText}
          </div>
        )}
      </div>
    );
  },
);

export const Input = component;
